import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store';
import { selectActiveStudy, selectBlockWarnings } from '@/store/selectors';
import { StudyModeBar } from './StudyModeBar';
import { ArmConfiguration } from './ArmConfiguration';
import { DataEntrySection } from './DataEntrySection';
import { AucComputationPanel } from './AucComputationPanel';
import { ResultCard } from './ResultCard';
import { ValidationPanel } from './ValidationPanel';
import { ScreenEmpty } from '@/components/ui/ScreenEmpty';

/**
 * Calculator screen — the primary workflow (P1.5).
 * Two-column layout: left = inputs (4 collapsibles), right = outputs.
 */
export function CalculatorScreen() {
  const study = useStore(selectActiveStudy);
  const setActiveScreen = useStore((s) => s.setActiveScreen);
  const compute = useStore((s) => s.compute);
  const saveStudy = useStore((s) => s.saveStudy);
  const pushToast = useStore((s) => s.pushToast);
  // H5: when blocked, render Validation above Result so the actionable
  // warnings are visible without scrolling past the blocked banner.
  const blocks = useStore(useShallow(selectBlockWarnings));

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        compute();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveStudy();
        pushToast('Study saved.', 'info');
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [compute, saveStudy, pushToast]);

  if (!study) {
    return (
      <ScreenEmpty
        body="No active study."
        primary={{ label: 'Go to Studies', onClick: () => setActiveScreen('studies') }}
      />
    );
  }

  const validationFirst = blocks.length > 0;

  return (
    <div className="calculator-screen">
      <StudyModeBar />
      <div className="calculator-grid">
        <div className="calculator-column calculator-inputs">
          <ArmConfiguration />
          <DataEntrySection />
          <AucComputationPanel />
        </div>
        <div className="calculator-column calculator-outputs">
          {validationFirst ? (
            <>
              <ValidationPanel />
              <ResultCard />
            </>
          ) : (
            <>
              <ResultCard />
              <ValidationPanel />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
